import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "/backend/public";

function CourseCategoryManager() {
  const [meta, setMeta] = useState({ academy_categories: [], models: [], categories: [], franchises: [] });
  const [categoryForm, setCategoryForm] = useState({ academy_category: "", course_model_id: "", name: "", description: "", is_active: true });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [assignForm, setAssignForm] = useState({ assign_scope: "single", academy_category: "", franchise_id: "", course_category_id: "" });
  const [filters, setFilters] = useState({ academy_category: "", search: "", course_model_id: "" });
  const [data, setData] = useState({ categories: [], assignments: [] });
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadMeta = async () => {
    const res = await axios.get(`${API}/api/course-setup/meta`);
    setMeta(res.data || { academy_categories: [], models: [], categories: [], franchises: [] });
  };

  const loadData = async () => {
    const res = await axios.get(`${API}/api/course-categories-admin`, { params: filters });
    setData(res.data || { categories: [], assignments: [] });
  };

  useEffect(() => {
    Promise.all([loadMeta(), loadData()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(editingCategoryId ? `${API}/api/course-categories-admin/${editingCategoryId}` : `${API}/api/course-categories-admin`, {
        ...categoryForm,
        is_active: categoryForm.is_active ? 1 : 0,
      });
      setCategoryForm({ academy_category: "", course_model_id: "", name: "", description: "", is_active: true });
      setEditingCategoryId(null);
      await Promise.all([loadMeta(), loadData()]);
      alert(editingCategoryId ? "Course category updated successfully." : "Course category saved successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save course category.");
    } finally {
      setSaving(false);
    }
  };

  const assignCategory = async (event) => {
    event.preventDefault();
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/api/course-category-assignments`, assignForm);
      setAssignForm({ assign_scope: "single", academy_category: "", franchise_id: "", course_category_id: "" });
      await loadData();
      alert(res.data?.message || "Category assigned successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to assign category.");
    } finally {
      setAssigning(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!(await sweetConfirm("Are you sure you want to delete this course category?", { confirmButtonText: "Yes, Delete" }))) return;
    try {
      await axios.delete(`${API}/api/course-categories-admin/${id}`);
      if (editingCategoryId === id) {
        setEditingCategoryId(null);
        setCategoryForm({ academy_category: "", course_model_id: "", name: "", description: "", is_active: true });
      }
      await Promise.all([loadMeta(), loadData()]);
      alert("Course category deleted successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete course category.");
    }
  };

  const visibleModules = meta.models.filter((item) => !categoryForm.academy_category || item.academy_category === categoryForm.academy_category);
  const filterModules = meta.models.filter((item) => !filters.academy_category || item.academy_category === filters.academy_category);
  const assignableFranchises = meta.franchises.filter((item) => !assignForm.academy_category || item.academy_category === assignForm.academy_category);
  const assignableCategories = meta.categories.filter((item) => !assignForm.academy_category || item.academy_category === assignForm.academy_category);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h3 className="mb-1">Course Category</h3>
            <p className="text-muted mb-4">Static academy category select করে, তার under এর module এর against category তৈরি হবে।</p>
            <form onSubmit={saveCategory}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Static Academy Category</label>
                  <select className="form-select" value={categoryForm.academy_category} onChange={(e) => setCategoryForm((prev) => ({ ...prev, academy_category: e.target.value, course_model_id: "" }))} required>
                    <option value="">Select Academy Category</option>
                    {meta.academy_categories.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Course Module</label>
                  <select className="form-select" value={categoryForm.course_model_id} onChange={(e) => setCategoryForm((prev) => ({ ...prev, course_model_id: e.target.value }))} required>
                    <option value="">Select Module</option>
                    {visibleModules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Category Name</label>
                  <input className="form-control" value={categoryForm.name} onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))} required />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <input className="form-control" value={categoryForm.description} onChange={(e) => setCategoryForm((prev) => ({ ...prev, description: e.target.value }))} />
                </div>
              </div>
              <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={categoryForm.is_active} onChange={(e) => setCategoryForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
                  <label className="form-check-label">{categoryForm.is_active ? "Active" : "Inactive"}</label>
                </div>
                <button className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : editingCategoryId ? "Update Category" : "Save Category"}</button>
                {editingCategoryId ? <button type="button" className="btn btn-outline-secondary" onClick={() => { setEditingCategoryId(null); setCategoryForm({ academy_category: "", course_model_id: "", name: "", description: "", is_active: true }); }}>Cancel Edit</button> : null}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h5 className="mb-3">Assign Category To Franchise</h5>
            <form onSubmit={assignCategory}>
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Assign Mode</label>
                  <select
                    className="form-select"
                    value={assignForm.assign_scope}
                    onChange={(e) => setAssignForm((prev) => ({ ...prev, assign_scope: e.target.value, academy_category: "", franchise_id: "" }))}
                  >
                    <option value="single">Single Franchise</option>
                    <option value="academy">All Under Academy</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Static Academy</label>
                  <select
                    className="form-select"
                    value={assignForm.academy_category}
                    onChange={(e) => setAssignForm((prev) => ({ ...prev, academy_category: e.target.value, franchise_id: "", course_category_id: "" }))}
                    required={assignForm.assign_scope === "academy"}
                  >
                    <option value="">Select Academy Category</option>
                    {meta.academy_categories.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Franchise</label>
                  <select
                    className="form-select"
                    value={assignForm.franchise_id}
                    onChange={(e) => setAssignForm((prev) => ({ ...prev, franchise_id: e.target.value }))}
                    required={assignForm.assign_scope === "single"}
                    disabled={assignForm.assign_scope !== "single"}
                  >
                    <option value="">{assignForm.assign_scope === "academy" ? "Auto assign to all franchise" : "Select Franchise"}</option>
                    {assignableFranchises.map((item) => <option key={item.id} value={item.id}>{item.centre_name} {item.centre_code ? `(${item.centre_code})` : ""}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={assignForm.course_category_id} onChange={(e) => setAssignForm((prev) => ({ ...prev, course_category_id: e.target.value }))} required>
                    <option value="">Select Category</option>
                    {assignableCategories.map((item) => <option key={item.id} value={item.id}>{item.academy_category ? `${item.academy_category} / ` : ""}{item.model_name ? `${item.model_name} / ` : ""}{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <div className="small text-muted mb-2">
                    {assignForm.assign_scope === "academy"
                      ? "Selected static academy er sob franchise একসাথে assign হবে."
                      : "একটা franchise-এ manual assign হবে."}
                  </div>
                  <button className="btn btn-outline-dark w-100" disabled={assigning}>{assigning ? "Assigning..." : "Assign"}</button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <select className="form-select" value={filters.academy_category} onChange={(e) => setFilters((prev) => ({ ...prev, academy_category: e.target.value, course_model_id: "" }))}>
                  <option value="">All Academy Categories</option>
                  {meta.academy_categories.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <input className="form-control" placeholder="Search category" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <select className="form-select" value={filters.course_model_id} onChange={(e) => setFilters((prev) => ({ ...prev, course_model_id: e.target.value }))}>
                  <option value="">All Modules</option>
                  {filterModules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="col-md-12">
                <button className="btn btn-outline-primary w-100" onClick={loadData}>Apply Filter</button>
              </div>
            </div>

            <div className="table-responsive mb-4">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Academy Category</th>
                    <th>Module</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.categories || []).map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.academy_category || "-"}</td>
                      <td>{item.model_name || "-"}</td>
                      <td>{item.name}</td>
                      <td>{item.description || "-"}</td>
                      <td><span className={`badge ${item.is_active ? "bg-success" : "bg-secondary"}`}>{item.is_active ? "Active" : "Inactive"}</span></td>
                      <td>
                        <div className="d-flex gap-2">
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setEditingCategoryId(item.id); setCategoryForm({ academy_category: item.academy_category || "", course_model_id: String(item.course_model_id || ""), name: item.name || "", description: item.description || "", is_active: Boolean(item.is_active) }); window.scrollTo({ top: 0, behavior: "smooth" }); }} title="Edit">
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteCategory(item.id)} title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!(data.categories || []).length && <tr><td colSpan="7" className="text-center text-muted py-4">No category found.</td></tr>}
                </tbody>
              </table>
            </div>

            <h5 className="mb-3">Assigned Categories</h5>
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Franchise</th>
                    <th>Academy Category</th>
                    <th>Module</th>
                    <th>Category</th>
                    <th>Assigned Via</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.assignments || []).map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.centre_name || "-"}</td>
                      <td>{item.academy_category || item.franchise_academy_category || "-"}</td>
                      <td>{item.model_name || "-"}</td>
                      <td>{item.category_name || "-"}</td>
                      <td><span className="badge bg-info text-dark">{item.assigned_via || "manual"}</span></td>
                    </tr>
                  ))}
                  {!(data.assignments || []).length && <tr><td colSpan="6" className="text-center text-muted py-4">No assignment found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CourseCategoryManager;



